/**
 * Smart Analytics Dashboard
 * 
 * Features:
 * - Echtzeit-Umsatzanalysen
 * - Beste Verkaufszeiten
 * - Produkt-Performance
 * - KI-Insights
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Clock, 
  Package, AlertCircle, Lightbulb, RefreshCw, Calendar,
  BarChart3, ArrowUp, ArrowDown, Minus, Star
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Trend Icon Component
const TrendIcon = ({ trend, size = 16 }) => {
  if (trend === 'up') return <ArrowUp className="text-emerald-400" size={size} />;
  if (trend === 'down') return <ArrowDown className="text-red-400" size={size} />;
  return <Minus className="text-slate-400" size={size} />;
};

// Metric Card Component
const MetricCard = ({ title, value, change, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30",
    green: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30"
  };
  
  const iconColors = {
    blue: "text-blue-400",
    green: "text-emerald-400",
    amber: "text-amber-400",
    purple: "text-purple-400"
  };
  
  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]} border`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {change !== null && change !== undefined && (
              <div className="flex items-center gap-1 mt-1">
                {change > 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                ) : change < 0 ? (
                  <TrendingDown className="w-4 h-4 text-red-400" />
                ) : null}
                <span className={`text-sm ${change > 0 ? 'text-emerald-400' : change < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {change > 0 ? '+' : ''}{change?.toFixed(1)}%
                </span>
                <span className="text-xs text-slate-500">vs. Vorperiode</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-slate-800/50 flex items-center justify-center ${iconColors[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState(null);
  const [peakTimes, setPeakTimes] = useState(null);
  const [insights, setInsights] = useState(null);

  // Load data
  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    loadData();
  }, [sessionToken, period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      // Load all data in parallel
      const [dashboardRes, productsRes, peakRes, insightsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/dashboard?period=${period}`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/products/performance?period=${period}&limit=10`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/peak-times?period=${period}`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/insights`, { headers })
      ]);
      
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (peakRes.ok) setPeakTimes(await peakRes.json());
      if (insightsRes.ok) setInsights(await insightsRes.json());
      
    } catch (error) {
      console.error('Analytics error:', error);
      toast.error('Fehler beim Laden der Analysen');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  // Period labels
  const periodLabels = {
    today: 'Heute',
    yesterday: 'Gestern',
    week: 'Diese Woche',
    month: 'Dieser Monat',
    year: 'Dieses Jahr'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-400" />
          <p className="text-slate-400">Lade Analysen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Header */}
      <header className="bg-[#111C32] border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              Smart Analytics
            </h1>
            <p className="text-sm text-slate-400">
              Ihre Geschäftszahlen auf einen Blick
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="today">Heute</SelectItem>
                <SelectItem value="yesterday">Gestern</SelectItem>
                <SelectItem value="week">Diese Woche</SelectItem>
                <SelectItem value="month">Dieser Monat</SelectItem>
                <SelectItem value="year">Dieses Jahr</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={loadData}
              className="border-slate-700 text-slate-300"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Umsatz"
            value={formatCurrency(dashboard?.sales?.total_revenue)}
            change={dashboard?.sales?.comparison_percent}
            icon={DollarSign}
            color="green"
          />
          <MetricCard
            title="Bestellungen"
            value={dashboard?.sales?.total_orders || 0}
            change={null}
            icon={ShoppingCart}
            color="blue"
          />
          <MetricCard
            title="Ø Warenkorb"
            value={formatCurrency(dashboard?.sales?.average_order_value)}
            change={null}
            icon={Package}
            color="purple"
          />
          <MetricCard
            title="Top-Stunde"
            value={dashboard?.peak_hours?.[0] ? `${dashboard.peak_hours[0].hour}:00` : '-'}
            change={null}
            icon={Clock}
            color="amber"
          />
        </div>

        {/* Insights */}
        {dashboard?.insights?.length > 0 && (
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {dashboard.insights.map((insight, idx) => (
                  <Badge 
                    key={idx} 
                    variant="secondary" 
                    className="bg-slate-800 text-slate-200 py-2 px-3 text-sm"
                  >
                    {insight}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Top Products */}
          <Card className="lg:col-span-2 bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-400" />
                Top Produkte
              </CardTitle>
              <CardDescription className="text-slate-400">
                Bestseller nach Umsatz
              </CardDescription>
            </CardHeader>
            <CardContent>
              {products?.products?.length > 0 ? (
                <div className="space-y-3">
                  {products.products.slice(0, 8).map((product, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? 'bg-amber-500/20 text-amber-400' :
                          idx === 1 ? 'bg-slate-400/20 text-slate-300' :
                          idx === 2 ? 'bg-amber-700/20 text-amber-600' :
                          'bg-slate-700/50 text-slate-400'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-white">{product.product_name}</p>
                          <p className="text-sm text-slate-400">{product.total_sold}x verkauft</p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <span className="text-emerald-400 font-bold">
                          {formatCurrency(product.total_revenue)}
                        </span>
                        <TrendIcon trend={product.trend} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Verkaufsdaten für diesen Zeitraum</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Peak Times */}
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Beste Zeiten
              </CardTitle>
              <CardDescription className="text-slate-400">
                Wann kommen die meisten Bestellungen?
              </CardDescription>
            </CardHeader>
            <CardContent>
              {peakTimes?.best_times?.length > 0 ? (
                <div className="space-y-3">
                  {peakTimes.best_times.slice(0, 6).map((time, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm w-20">
                          {time.weekday_name?.slice(0, 2) || 'Mo'}
                        </span>
                        <span className="text-white font-medium">
                          {time.hour}:00 Uhr
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={(time.orders / (peakTimes.best_times[0]?.orders || 1)) * 100} 
                          className="w-20 h-2"
                        />
                        <span className="text-sm text-slate-400 w-12 text-right">
                          {time.orders}x
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Daten verfügbar</p>
                </div>
              )}
              
              {peakTimes?.recommendations?.length > 0 && (
                <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  {peakTimes.recommendations.map((rec, idx) => (
                    <p key={idx} className="text-sm text-blue-300">{rec}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Insights Section */}
        {insights?.insights?.length > 0 && (
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                KI-Geschäftsanalyse
              </CardTitle>
              <CardDescription className="text-slate-400">
                Automatisch generierte Empfehlungen für Ihr Geschäft
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {insights.insights.map((insight, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      insight.type === 'warning' 
                        ? 'bg-amber-500/10 border-amber-500/30' 
                        : 'bg-slate-800/50 border-slate-700'
                    }`}
                  >
                    <div className="text-2xl mb-2">{insight.icon}</div>
                    <p className="text-sm text-slate-400">{insight.title}</p>
                    <p className="text-lg font-bold text-white">{insight.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{insight.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Performance Summary */}
        {products?.summary && (
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Produkt-Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-400">{products.summary.rising}</p>
                  <p className="text-sm text-slate-400">Steigend</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <Minus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-300">{products.summary.stable}</p>
                  <p className="text-sm text-slate-400">Stabil</p>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <TrendingDown className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-400">{products.summary.falling}</p>
                  <p className="text-sm text-slate-400">Fallend</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </main>
    </div>
  );
}
